
export const STEPS = [
    "Review & Prep",
    "Contact & Address",
    "Shipping Details",
    "Cooler Info",
    "Comments & Attachments",
];

export const SAMPLE_MATRIX_OPTIONS = [
    "Antifreeze", "Bathing Beach", "Drinking Water", "Dry Sludge", "Ground Water",
    "Non-Potable Water", "Oil", "Other", "Pools", "Sludge", "Soil", "Solid",
    "Stormwater", "Surface Water", "Wastewater", "Wet Sludge", "Wipes",
];

export const SHIP_BY_OPTIONS = [
    "UPS",
    "Pickup Madison Lab",
    "Pickup Carmel Lab",
    "Pickup Lafayette Lab",
    "FedEx",
    "Routine Route-Black",
    "Routine Route-Black/Gold",
    "Routine Route-Orange",
    "Routine Route-Blue",
    "Routine Route-Red",
    "Routine Route-Purple",
    "Routine Route-Green",
    "Routine Route",
    "ClientWalkIn",
    "PickUp",
    "UPS - with Return Label",
];

// Clamps to the last day of the target month, matching C# DateTime.AddMonths
const addMonths = (date, months) => {
    const result = new Date(date.getTime());
    const day = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + months);
    const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(day, lastDay));
    return result;
};

const addDays = (date, days) => {
    const result = new Date(date.getTime());
    result.setDate(result.getDate() + days);
    return result;
};

export const nextScheduleDate = (date, frequency) => {
    switch (frequency) {
        case "Daily": return addDays(date, 1);
        case "Weekly": return addDays(date, 7);
        case "Monthly": return addMonths(date, 1);
        case "Quarterly": return addMonths(date, 3);
        case "Biannual": return addMonths(date, 6);
        case "Yearly": return addMonths(date, 12);
        default: return null;
    }
};

// Mirrors GenerateOrderDates on the server, including skipping the first occurrence
// only when duplicating an existing order.
export const calculateScheduledOrderCount = (startDateTime, endDateTime, scheduleFrequency, skipFirstInterval = false) => {
    if (!startDateTime || !endDateTime || !scheduleFrequency) return 0;

    const startDate = new Date(startDateTime);
    const endDate = new Date(endDateTime);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;
    if (endDate < startDate) return 0;
    if (scheduleFrequency === "Once") return 1;

    let count = 0;
    let current = skipFirstInterval ? nextScheduleDate(startDate, scheduleFrequency) : startDate;
    if (!current) return 0;

    while (current <= endDate) {
        count++;
        current = nextScheduleDate(current, scheduleFrequency);
    }

    return count;
};

const formatDateValue = (date) => (date ? date.split("T")[0] : "");

export const buildInitialState = (bottleOrderData) => ({
    orderNumber: bottleOrderData?.orderNumber || 0,
    accountNumber: bottleOrderData?.accountNumber || "",
    shippingName: bottleOrderData?.shippingName || "",
    shippingContact: bottleOrderData?.shippingContact || "",
    shippingAddress: bottleOrderData?.shippingAddress || "",
    shippingAddress2: bottleOrderData?.shippingAddress2 || "",
    shippingCity: bottleOrderData?.shippingCity || "",
    shippingState: bottleOrderData?.shippingState || "",
    shippingZip: bottleOrderData?.shippingZip || "",
    shippingPhone: bottleOrderData?.shippingPhone || "",
    shippingFax: bottleOrderData?.shippingFax || "",
    dateCreated: formatDateValue(bottleOrderData?.dateCreated),
    shippingDate: formatDateValue(bottleOrderData?.shippingDate),
    dueDate: formatDateValue(bottleOrderData?.dueDate),
    kitPrepDueDate: formatDateValue(bottleOrderData?.kitPrepDueDate),
    createdBy: bottleOrderData?.createdBy || "",
    description: bottleOrderData?.description || "",
    sampleMatrix: bottleOrderData?.sampleMatrix || "",
    shippingStatus: bottleOrderData?.shippingStatus || "",
    trackingNumber: bottleOrderData?.trackingNumber || "",
    trackingComment: bottleOrderData?.trackingComment || "",
    comments: bottleOrderData?.comments || "",
    shippingEmail: bottleOrderData?.shippingEmail || "",
    shippingInstructions: bottleOrderData?.shippingInstructions || "",
    recordid: bottleOrderData?.recordid || 0,
    dateReceived: formatDateValue(bottleOrderData?.dateReceived),
    coolerNumber: bottleOrderData?.coolerNumber || "",
    coolerStatus: bottleOrderData?.coolerStatus || "",
    dateReceivedCooler: formatDateValue(bottleOrderData?.dateReceivedCooler),
    preparedBy: bottleOrderData?.preparedBy || "",
    preparedByDate: formatDateValue(bottleOrderData?.preparedByDate),
    reviewedBy: bottleOrderData?.reviewedBy || "",
    reviewedByDate: formatDateValue(bottleOrderData?.reviewedByDate),
    shipBy: bottleOrderData?.shipBy || "",
    shippingDueDateFirm: bottleOrderData?.shippingDueDateFirm || false,
    orderReviewedBy: bottleOrderData?.orderReviewedBy || "",
    orderReviewedByDate: formatDateValue(bottleOrderData?.orderReviewedByDate),
    useMaintenance: bottleOrderData?.useMaintenance || false,
    orderedBy: bottleOrderData?.orderedBy || "",
    quoteNumber: bottleOrderData?.quoteNumber || "",
    sendToIdem: bottleOrderData?.sendToIdem || false,
    routineShipmentNotes: bottleOrderData?.routineShipmentNotes || "",
});

export const sanitizeDates = (data) => {
    const newData = { ...data };
    const dateFields = [
        "dateCreated", "shippingDate", "dueDate", "dateReceived",
        "dateReceivedCooler", "preparedByDate", "reviewedByDate",
        "orderReviewedByDate", "kitPrepDueDate",
    ];
    dateFields.forEach((field) => {
        if (newData[field] === "" || newData[field] === undefined) {
            newData[field] = null;
        }
    });
    return newData;
};

export const fieldSx = {
    "& .MuiInputBase-input": { fontSize: "0.875rem" },
    "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};

export const sectionHeadingSx = {
    fontWeight: 700,
    color: "primary.main",
    fontSize: "1.15rem",
    mb: 2,
};

export const subSectionSx = {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "text.secondary",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    mb: 1,
};

export const ACTION_CONFIG = (userInitials, today) => ({
    markAsReviewed: {
        title: "Mark Order as Reviewed?",
        message: `This will set the Bottle Order Status to "Approved", record your initials (${userInitials}), and stamp today's date. Continue?`,
        confirmLabel: "Mark as Reviewed",
        confirmColor: "primary",
        apply: (prev) => ({
            ...prev,
            shippingStatus: "Approved",
            orderReviewedBy: userInitials,
            orderReviewedByDate: today,
        }),
    },
    markAsBuilt: {
        title: "Mark Kit as Built?",
        message: `This will set the Bottle Order Status to "Kit Built", record your initials (${userInitials}), and stamp today's date. Continue?`,
        confirmLabel: "Mark as Built",
        confirmColor: "success",
        apply: (prev) => ({
            ...prev,
            shippingStatus: "Kit Built",
            preparedBy: userInitials,
            preparedByDate: today,
        }),
    },
    markKitReviewed: {
        title: "Mark Kit as Reviewed?",
        message: `This will set the Bottle Order Status to "Kit Reviewed", record your initials (${userInitials}), and stamp today's date. Continue?`,
        confirmLabel: "Mark Kit as Reviewed",
        confirmColor: "warning",
        apply: (prev) => ({
            ...prev,
            shippingStatus: "Kit Reviewed",
            reviewedBy: userInitials,
            reviewedByDate: today,
        }),
    },
});